import type { AnyFieldApi } from '@tanstack/react-form';

import {
  DateBox,
  dateToIso,
  dateToIsoDateTime,
  datePairToRange,
  isoDateTimeToDate,
  isoToDate,
  rangeToDatePair,
  type DateBoxProps,
  type DatePair,
} from '@/shared/ui';

/** Stored representation of a date form value. */
export type DateValueMode = 'string' | 'date';

export interface DateFieldProps extends Omit<
  DateBoxProps,
  'value' | 'onChange' | 'error' | 'onBlur'
> {
  /** TanStack Form field API. */
  field: AnyFieldApi;
  /**
   * How the value is stored on the form field:
   * - `'string'` (default) — the canonical string (`"YYYY-MM-DD"`, range `"A..B"`, `…T09:40`).
   * - `'date'` — a native `Date | null` (single) or `[Date | null, Date | null]` (range). Open ∞
   *   ends collapse to `null`; a form needing ∞ semantics should stay in `'string'` mode.
   */
  valueMode?: DateValueMode;
}

function fieldError(field: AnyFieldApi): string | undefined {
  return field.state.meta.isTouched
    ? (field.state.meta.errors[0]?.message as string | undefined)
    : undefined;
}

/**
 * Form-bound date field: bridges a TanStack Form field to `DateBox`, translating the Box's canonical
 * string to/from the form's stored representation (`valueMode`). Presets `DateRangeField`,
 * `DateTimeField`, `DateTimeRangeField` lock `mode` / `withTime`.
 */
export function DateField({
  field,
  valueMode = 'string',
  mode = 'single',
  withTime = false,
  ...rest
}: DateFieldProps) {
  const common = {
    ...rest,
    mode,
    withTime,
    error: fieldError(field),
    onBlur: field.handleBlur,
  } satisfies Partial<DateBoxProps>;

  if (valueMode === 'date') {
    if (mode === 'range') {
      const pair = (field.state.value as DatePair | null | undefined) ?? [null, null];
      return (
        <DateBox
          {...common}
          value={datePairToRange(pair, { withTime })}
          onChange={(next) => field.handleChange(rangeToDatePair(next, { withTime }))}
        />
      );
    }
    const date = (field.state.value as Date | null | undefined) ?? null;
    const toIso = withTime ? dateToIsoDateTime : dateToIso;
    const toDate = withTime ? isoDateTimeToDate : isoToDate;
    return (
      <DateBox
        {...common}
        value={toIso(date)}
        onChange={(next) => field.handleChange(toDate(next))}
      />
    );
  }

  return (
    <DateBox
      {...common}
      value={(field.state.value as string | undefined) ?? ''}
      onChange={(next) => field.handleChange(next)}
    />
  );
}

export type DateRangeFieldProps = Omit<DateFieldProps, 'mode'>;
export function DateRangeField(props: DateRangeFieldProps) {
  return <DateField {...props} mode="range" />;
}

export type DateTimeFieldProps = Omit<DateFieldProps, 'withTime'>;
export function DateTimeField(props: DateTimeFieldProps) {
  return <DateField {...props} withTime />;
}

export type DateTimeRangeFieldProps = Omit<DateFieldProps, 'mode' | 'withTime'>;
export function DateTimeRangeField(props: DateTimeRangeFieldProps) {
  return <DateField {...props} mode="range" withTime />;
}
